/**
 * Typed error model for the API seam.
 *
 * Every non-2xx response is normalized into an `ApiError` carrying the backend
 * error envelope `{ code, message, requestId }` plus the HTTP `status`. Callers
 * always receive this stable shape — even when the server returns a malformed or
 * empty body (the client synthesizes an envelope in that case).
 */

/** Machine-readable error codes from the backend envelope. */
export type ApiErrorCode =
  | 'unauthorized'
  | 'too_many_requests'
  | 'bad_request'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'unprocessable_entity'
  | 'internal_error'
  // Client-synthesized when the network fails before a response is received.
  | 'network_error';

/** Optional per-field validation detail (only present on some 422 responses). */
export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly requestId: string;
  readonly status: number;
  readonly fields?: FieldError[];

  constructor(params: {
    code: ApiErrorCode;
    message: string;
    requestId: string;
    status: number;
    fields?: FieldError[];
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.requestId = params.requestId;
    this.status = params.status;
    if (params.fields) {
      this.fields = params.fields;
    }
    // Restore prototype chain for `instanceof` across transpilation targets.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Thrown specifically on HTTP 401. A dedicated subclass lets route guards and the
 * auth context redirect to /sign-in without inspecting `code`.
 */
export class UnauthorizedError extends ApiError {
  constructor(params: { message: string; requestId: string; fields?: FieldError[] }) {
    super({ code: 'unauthorized', status: 401, ...params });
    this.name = 'UnauthorizedError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

export function isUnauthorizedError(err: unknown): err is UnauthorizedError {
  return err instanceof UnauthorizedError;
}
